// v229 admin page for the CNPG ImageCatalog mirror (item 46 of
// PRODUCT-ROADMAP.md, "CNPG ImageCatalog-Mirror"; item 52 in the
// recommended delivery order).
//
// Three cards, top to bottom:
//   1. Approved versions matrix — every row from cnpg_image_catalog
//      with inline approval toggle, EOL editor, and last-synced.
//   2. Sync status + settings — enable/disable, sync interval,
//      approved-majors filter, mirror image base, "Sync now" button,
//      and the most-recent-run banner.
//   3. Generated CR preview — live YAML the operator can copy or
//      download into a GitOps repo.

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Box,
  CheckCircle2,
  Copy,
  Database,
  Download,
  RefreshCw,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useCNPGSettings,
  useCNPGCatalog,
  useCNPGYAML,
  updateCNPGSettings,
  patchCNPGRow,
  kickCNPGSync,
  type CNPGSettings,
} from '../../api/cnpg';

const LABEL_CLASS = 'text-sm text-fg-muted block mb-1.5';

function isoDate(d?: string | null): string {
  if (!d) return '';
  return d.slice(0, 10);
}

function fmtRel(d?: string | null): string {
  if (!d) return 'never';
  const t = new Date(d).getTime();
  const diff = Date.now() - t;
  if (Number.isNaN(diff)) return d;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function SyncStatusIndicator({ status, count }: { status?: string; count?: number }) {
  if (status === 'ok') {
    return (
      <span className="text-success inline-flex items-center gap-1">
        <CheckCircle2 className="h-3.5 w-3.5" />
        ok ({count} versions)
      </span>
    );
  }
  if (status === 'fail') {
    return (
      <span className="text-danger inline-flex items-center gap-1">
        <AlertTriangle className="h-3.5 w-3.5" />
        fail
      </span>
    );
  }
  return <span>{status || '—'}</span>;
}

export function AdminCNPGCatalog() {
  const settings = useCNPGSettings();
  const catalog = useCNPGCatalog();
  const yaml = useCNPGYAML();

  const [draft, setDraft] = useState<CNPGSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Hydrate the draft once settings arrive; preserve user edits.
  useEffect(() => {
    if (settings.data && !draft) {
      setDraft(settings.data);
    }
  }, [settings.data, draft]);

  const rows = catalog.data?.rows ?? [];

  const approvedCount = useMemo(
    () => rows.filter((r) => r.approved).length,
    [rows],
  );

  async function saveSettings() {
    if (!draft) return;
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      await updateCNPGSettings({
        enabled: draft.enabled,
        sync_interval_sec: draft.sync_interval_sec,
        upstream_image: draft.upstream_image,
        approved_majors: draft.approved_majors,
        mirror_image_base: draft.mirror_image_base,
        catalog_name: draft.catalog_name,
      });
      setOkMsg('Settings saved.');
      await Promise.all([settings.mutate(), catalog.mutate(), yaml.mutate()]);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function runSyncNow() {
    setBusy(true);
    setErrorMsg(null);
    setOkMsg(null);
    try {
      const next = await kickCNPGSync();
      setDraft(next);
      if (next.last_sync_status === 'fail') {
        setErrorMsg(`Sync failed: ${next.last_sync_error}`);
      } else if (next.last_sync_status === 'skipped') {
        setOkMsg(`Sync skipped: ${next.last_sync_error || 'gate denied'}.`);
      } else {
        setOkMsg(`Sync complete: ${next.last_sync_count} versions matched.`);
      }
      await Promise.all([settings.mutate(), catalog.mutate(), yaml.mutate()]);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toggleApproved(id: string, current: boolean) {
    try {
      await patchCNPGRow(id, { approved: !current });
      await Promise.all([catalog.mutate(), yaml.mutate()]);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  async function setEOL(id: string, eol: string) {
    try {
      if (eol === '') {
        await patchCNPGRow(id, { clear_eol_date: true });
      } else {
        await patchCNPGRow(id, { eol_date: new Date(eol + 'T00:00:00Z').toISOString() });
      }
      await catalog.mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  async function copyYAML() {
    if (!yaml.data) return;
    try {
      await navigator.clipboard.writeText(yaml.data);
      setOkMsg('YAML copied to clipboard.');
    } catch {
      setOkMsg('Clipboard unavailable; use the Download button instead.');
    }
  }

  function downloadYAML() {
    if (!yaml.data) return;
    const blob = new Blob([yaml.data], { type: 'application/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${draft?.catalog_name || 'cnpg-catalog'}.yaml`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function setMajorsFromInput(value: string) {
    if (!draft) return;
    const parsed = value
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => Number.parseInt(s, 10))
      .filter((n) => Number.isFinite(n));
    setDraft({ ...draft, approved_majors: parsed });
  }

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <Database className="h-5 w-5 text-nebula" />
          CNPG ImageCatalog mirror
        </span>
      }
      description="Mirror CloudNativePG's approved Postgres image line and emit a ready-to-check-in ClusterImageCatalog CR. Disabled by default; enable below to start polling the upstream registry."
    >
      {settings.error && (
        <Card className="mb-4 border-danger/40">
          <CardBody>
            <p className="text-sm text-danger">
              Failed to load settings: {String(settings.error)}
            </p>
          </CardBody>
        </Card>
      )}
      {(errorMsg || okMsg) && (
        <Card className={errorMsg ? 'mb-4 border-danger/40' : 'mb-4 border-success/40'}>
          <CardBody>
            <p className={errorMsg ? 'text-sm text-danger' : 'text-sm text-success'}>
              {errorMsg || okMsg}
            </p>
          </CardBody>
        </Card>
      )}

      {/* Card 1 — sync status + settings */}
      <Card className="mb-6">
        <CardHeader
          title="Sync settings"
          description="Polling cadence, upstream image, and the approved Postgres majors that the worker should track."
          actions={
            <Button
              variant="secondary"
              size="sm"
              onClick={runSyncNow}
              disabled={busy}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Sync now
            </Button>
          }
        />
        <CardBody>
          {draft && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm text-fg-muted">
                    <input
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(e) =>
                        setDraft({ ...draft, enabled: e.target.checked })
                      }
                    />
                    <span>
                      Enable upstream poll (calls{' '}
                      <code className="text-xs">
                        {draft.upstream_image || 'ghcr.io/cloudnative-pg/postgresql'}
                      </code>{' '}
                      via OCI tags/list)
                    </span>
                  </label>
                </div>
                <div>
                  <label className={LABEL_CLASS} htmlFor="cnpg-upstream-image">
                    Upstream image
                  </label>
                  <Input
                    id="cnpg-upstream-image"
                    value={draft.upstream_image}
                    onChange={(e) =>
                      setDraft({ ...draft, upstream_image: e.target.value })
                    }
                    placeholder="ghcr.io/cloudnative-pg/postgresql"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS} htmlFor="cnpg-mirror-image-base">
                    Mirror image base (CRD output)
                  </label>
                  <Input
                    id="cnpg-mirror-image-base"
                    value={draft.mirror_image_base}
                    onChange={(e) =>
                      setDraft({ ...draft, mirror_image_base: e.target.value })
                    }
                    placeholder="orbitalreg.example.com/cnpg/postgres"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS} htmlFor="cnpg-catalog-name">
                    Catalog CR name
                  </label>
                  <Input
                    id="cnpg-catalog-name"
                    value={draft.catalog_name}
                    onChange={(e) =>
                      setDraft({ ...draft, catalog_name: e.target.value })
                    }
                    placeholder="orbitalreg-postgres-catalog"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS} htmlFor="cnpg-approved-majors">
                    Approved majors (comma-separated)
                  </label>
                  <Input
                    id="cnpg-approved-majors"
                    value={draft.approved_majors.join(', ')}
                    onChange={(e) => setMajorsFromInput(e.target.value)}
                    placeholder="16, 17"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS} htmlFor="cnpg-sync-interval">
                    Sync interval (seconds)
                  </label>
                  <Input
                    id="cnpg-sync-interval"
                    type="number"
                    min={60}
                    value={draft.sync_interval_sec}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        sync_interval_sec: Number.parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-fg-muted">
                <Badge tone={draft.enabled ? 'success' : 'neutral'}>
                  {draft.enabled ? 'enabled' : 'disabled'}
                </Badge>
                <span>Last sync: {fmtRel(draft.last_sync_at)}</span>
                <span>
                  Status:{' '}
                  <SyncStatusIndicator status={draft.last_sync_status} count={draft.last_sync_count} />
                </span>
                {draft.last_sync_error && (
                  <span className="text-fg-subtle text-xs">
                    {draft.last_sync_error}
                  </span>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <Button variant="primary" onClick={saveSettings} disabled={busy}>
                  Save settings
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Card 2 — approved versions matrix */}
      <Card className="mb-6">
        <CardHeader
          title="Approved versions"
          description={`${approvedCount} of ${rows.length} catalog rows are currently approved for the generated CR.`}
        />
        <CardBody>
          {rows.length === 0 ? (
            <div className="text-sm text-fg-muted">
              <p className="mb-2">
                The catalog is empty. The worker hasn't seen any matching tags yet —
                either the feature is disabled, the upstream image is unreachable,
                or no tags match your approved majors filter.
              </p>
              <p>
                Click <strong>Sync now</strong> above after enabling the worker to
                populate the table.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="py-2 pr-4">Major</th>
                    <th className="py-2 pr-4">Version</th>
                    <th className="py-2 pr-4">Last synced</th>
                    <th className="py-2 pr-4">EOL</th>
                    <th className="py-2 pr-4">Approved</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border/50">
                      <td className="py-2 pr-4 font-mono">{r.major}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {r.full_version}
                      </td>
                      <td className="py-2 pr-4 text-xs text-fg-muted">
                        {fmtRel(r.synced_at)}
                      </td>
                      <td className="py-2 pr-4">
                        <input
                          type="date"
                          className="bg-bg-inset text-fg text-xs px-2 py-1 rounded border border-border focus:border-accent"
                          defaultValue={isoDate(r.eol_date)}
                          onBlur={(e) => {
                            if (isoDate(r.eol_date) !== e.target.value) {
                              setEOL(r.id, e.target.value);
                            }
                          }}
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <button
                          className="text-xs underline"
                          onClick={() => toggleApproved(r.id, r.approved)}
                        >
                          {r.approved ? (
                            <Badge tone="success">approved</Badge>
                          ) : (
                            <Badge tone="neutral">excluded</Badge>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Card 3 — generated CR preview */}
      <Card>
        <CardHeader
          title="Generated ClusterImageCatalog"
          description="Drop this into your GitOps repo. The CNPG operator picks it up via cluster.spec.imageCatalogRef."
          actions={
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={copyYAML}>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <Button variant="secondary" size="sm" onClick={downloadYAML}>
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </div>
          }
        />
        <CardBody>
          {!draft?.mirror_image_base && (
            <p className="text-xs text-fg-subtle mb-3 flex items-center gap-1.5">
              <Box className="h-3.5 w-3.5" />
              Mirror image base is unset — the YAML below references the upstream
              image directly. Configure it above to substitute your OrbitalReg-side
              path.
            </p>
          )}
          <pre className="bg-bg-inset border border-border rounded-md p-4 text-xs font-mono overflow-x-auto whitespace-pre">
            {yaml.data ?? '# loading…'}
          </pre>
        </CardBody>
      </Card>
    </AdminShell>
  );
}
